import { z } from 'zod';

/**
 * A run's raw LLM transcript: every call the pipeline made, in the order they completed, with the
 * exact input it sent and the output it got back. A debugging instrument for tuning prompts,
 * recommendations and grounding — deliberately not part of `runSchema`, so the run blob the UI polls
 * every few seconds stays small (see `agentic_insights_transcript`).
 *
 * Temporary: this exists until the eval harness lands, and is expected to be deleted rather than
 * migrated. Hence a single version literal with no back-compat branches — a transcript that fails
 * the gate is dropped, and the run it belongs to is unaffected.
 */
export const CURRENT_TRANSCRIPT_SCHEMA_VERSION = 1 as const;

/**
 * These mirror `@tempo-io/backend-llm-client`'s wire types, which sir-dto cannot import (it is shared
 * with the frontend). `type` and `role` are bare strings rather than enums so a provider emitting a
 * new block or role kind still reads back.
 *
 * Deliberately NOT `.loose()`: a loose object infers an index signature, which llm-client's
 * `ContentBlock`/`Message` interfaces cannot satisfy, so the recorder could not write to it without a
 * cast. The cost is that an unmodelled top-level block key is stripped on read — acceptable, since
 * `input` (a tool call's arguments) and `content` (its result) are both kept as `unknown` and those
 * are the fields that carry the payloads worth reading.
 */
export const transcriptContentBlockSchema = z.object({
  type: z.string(),
  text: z.string().optional(),
  id: z.string().optional(),
  name: z.string().optional(),
  input: z.unknown().optional(),
  tool_use_id: z.string().optional(),
  content: z.unknown().optional(),
});

export const transcriptMessageSchema = z.object({
  role: z.string(),
  content: z.union([z.string(), z.array(transcriptContentBlockSchema)]),
});

export const transcriptUsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheReadInputTokens: z.number().int().nonnegative().optional(),
  cacheWriteInputTokens: z.number().int().nonnegative().optional(),
});

/**
 * One tool call the model made, with everything needed to judge it in one place: the arguments it
 * passed and what came back.
 *
 * `result` is lifted from the `tool_result` block that the NEXT call in the same conversation carried
 * in its `input` — the provider's own wire shape puts a call's arguments and its result in different
 * messages, so reading them together would otherwise mean correlating `tool_use.id` by hand across
 * the transcript. Absent when no result ever came back: the run ended on this turn, the loop
 * abandoned it, or the transcript hit its byte cap first. Nothing is lost either way — the raw
 * `tool_result` block stays in the following call's `input` regardless.
 */
export const transcriptToolInvocationSchema = z.object({
  /** The provider's `tool_use` id, which is what links this call to its result block. */
  id: z.string().optional(),
  name: z.string(),
  args: z.unknown().optional(),
  result: z.unknown().optional(),
});

/** `offered` is the toolset the call was given; `invoked` is what the model actually called. */
export const transcriptToolsSchema = z.object({
  offered: z.array(z.string()),
  invoked: z.array(transcriptToolInvocationSchema),
});

/**
 * The inputs that decide which passes run at all. Recorded because a pass making zero calls is
 * otherwise ambiguous — "not instrumented" reads the same as "correctly did nothing". A single-persona
 * org does no re-voice, and the actionability pass is off unless its judge count is raised.
 */
export const transcriptGatingSchema = z.object({
  findingCount: z.number().int().nonnegative(),
  /** Re-voice runs once per NON-core persona; zero here means the pass is a no-op by configuration. */
  nonCorePersonaCount: z.number().int().nonnegative(),
  /** `SIR_AGENTIC_INSIGHTS_ACTIONABILITY_JUDGES`; defaults to 0, which skips the pass entirely. */
  actionabilityJudgeCount: z.number().int().nonnegative(),
});

/** Per-pass roll-up. `skipped` carries why a pass made no calls, when the pipeline chose not to run it. */
/**
 * What persistence did on this run, recorded on the transcript because the decisions happen in code
 * after the model has answered and would otherwise be invisible to anyone reading the calls.
 */
export const transcriptContinuitySchema = z.object({
  standing: z.array(
    z.object({
      id: z.string(),
      headline: z.string(),
      sinceRunId: z.string(),
      lastSeenRunId: z.string(),
      missedRuns: z.number().int().nonnegative(),
    }),
  ),
  links: z.array(
    z.object({
      findingId: z.string(),
      continues: z.string(),
      declared: z.string(),
      outcome: z.enum(['accepted', 'demoted']),
      reason: z.string().optional(),
    }),
  ),
  retired: z.array(
    z.object({
      id: z.string(),
      reason: z.string(),
      note: z.string().optional(),
      decidedBy: z.enum(['model', 'code']),
    }),
  ),
  /** Findings the writer submitted (kept, updated or new) that a later critic discarded, and which
   * one. Continuity above is decided before the critics run, so without this a transcript reads
   * "kept 9" while the stored run holds 5. Absent on transcripts written before it was recorded. */
  dropped: z
    .array(
      z.object({
        findingId: z.string(),
        status: z.string(),
        continues: z.string().optional(),
        stage: z.string(),
      }),
    )
    .optional(),
});

export const transcriptPassSchema = z.object({
  calls: z.number().int().nonnegative(),
  skipped: z.string().optional(),
});

export const transcriptCallSchema = z.object({
  /** Completion order within the run, from 1. The `actionability` pass fans out concurrently, so
   * this is not always start order — compare `startedAt` when that matters. */
  seq: z.number().int().positive(),
  /** Which pipeline pass made the call: authoring | grounding | repair | plainVoice | reconcile | revoice | actionability | deliveryRisk. */
  pass: z.string(),
  startedAt: z.string(),
  durationMs: z.number().int().nonnegative(),
  systemPrompt: z.string(),
  /**
   * The inference options as they actually reached the provider, not as the pass was configured. The
   * recorder taps below `withInferenceOverrides`/`withPromptCaching` precisely so these are the
   * resolved values — `promptCaching` is on for the authoring loop only.
   */
  model: z.string().optional(),
  maxTokens: z.number().int().positive().optional(),
  promptCaching: z.boolean().optional(),
  /** Set when the harness forced this call to answer with the named tool (a pass out of turns or
   * context), so a forced submission can be told apart from one the model chose to make. */
  forceTool: z.string().optional(),
  tools: transcriptToolsSchema,
  input: z.array(transcriptMessageSchema),
  output: z
    .object({
      content: z.array(transcriptContentBlockSchema),
      stopReason: z.string(),
      usage: transcriptUsageSchema.optional(),
    })
    .nullable(),
  /**
   * Set when the call threw. Worth its own field: the agent loop retries a failed call and never
   * pushes the failed attempt into its message history, so a failure is invisible in the transcript
   * of any later call.
   */
  error: z.string().optional(),
});

export const transcriptSchema = z.object({
  version: z.literal(CURRENT_TRANSCRIPT_SCHEMA_VERSION),
  runId: z.string().min(1),
  organizationId: z.string().min(1),
  loopId: z.number().int().positive().optional(),
  /** The model the run was configured with. Per-call `model` is what each call actually used. */
  model: z.string(),
  /** Which run attempt produced this transcript — a retried run is a different attempt. */
  attempt: z.number().int().nonnegative(),
  /**
   * The deployed chart version (`APP_VERSION`) that produced the run, so a transcript can be tied to
   * the code that made it. Not a commit SHA: a locally-built dev deploy will not pin one.
   */
  codeVersion: z.string(),
  gating: transcriptGatingSchema,
  /** One entry per pipeline pass, including passes that ran nothing. */
  passes: z.record(z.string(), transcriptPassSchema),
  calls: z.array(transcriptCallSchema),
  /** True when recording stopped at the per-run byte cap; `calls` is then a prefix of the run. */
  truncated: z.boolean(),
  /** Absent on runs made before persistence, and on a run with no earlier run to stand on. */
  continuity: transcriptContinuitySchema.optional(),
});

export type TranscriptContentBlock = z.infer<typeof transcriptContentBlockSchema>;
export type TranscriptMessage = z.infer<typeof transcriptMessageSchema>;
export type TranscriptGating = z.infer<typeof transcriptGatingSchema>;
export type TranscriptPass = z.infer<typeof transcriptPassSchema>;
export type TranscriptToolInvocation = z.infer<typeof transcriptToolInvocationSchema>;
export type TranscriptTools = z.infer<typeof transcriptToolsSchema>;
export type TranscriptCall = z.infer<typeof transcriptCallSchema>;
export type Transcript = z.infer<typeof transcriptSchema>;
export type TranscriptContinuity = z.infer<typeof transcriptContinuitySchema>;
